/**
 * KNAPPEN. En komponent, sex utseenden — alla ur prototypen.
 *
 *   'stor'         .bigbtn        Spara, Godkänn, Ny arbetsorder. Mörk,
 *                                 fullbred. EN per skärm; är det två är den
 *                                 andra 'stor-tyst'.
 *   'stor-tyst'    .bigbtn.ghost  Sekundär fullbred (Lås veckan bredvid Spara).
 *   'snabb'        .qbtn          Två-tre små bredvid varandra. Använd
 *                                 <Snabbrad> som hölje.
 *   'piller'       .pill          Åtgärden i en rad: Godkänn, Påminn.
 *   'piller-mini'  .pill.mini     Samma, mindre. I .setting och .forslag.
 *   'ikon'         .iconbtn       Rund, bara ikon. KRÄVER `etikett`.
 *   'rapport'      .repbtn        Rutnätet Tid/Material/Mil/Utlägg på
 *                                 arbetsordern. Använd <Rapportrad>.
 *
 * TON på pillret: 'god' (redan gjort, inte klickbart) och 'varning' (något
 * fattas — "Påminn"). Samma två toner som brickan.
 *
 * TYP ÄR ALLTID 'button'. En knapp utan type inne i ett formulär är en
 * submit-knapp, och den skickar formuläret när montören ville lägga till en
 * rad. Bug som försvinner här och inte i tolv skärmar.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Ikon } from './Ikon';
import type { Ikonnamn } from './Ikon';

export type Knappvariant =
  | 'stor'
  | 'stor-tyst'
  | 'snabb'
  | 'piller'
  | 'piller-mini'
  | 'ikon'
  | 'rapport';

export type Knappton = 'normal' | 'god' | 'varning';

const KLASS: Record<Knappvariant, string> = {
  stor: 'bigbtn',
  'stor-tyst': 'bigbtn ghost',
  snabb: 'qbtn',
  piller: 'pill',
  'piller-mini': 'pill mini',
  ikon: 'iconbtn',
  rapport: 'repbtn',
};

const TON: Record<Knappton, string> = { normal: '', god: ' done', varning: ' warnp' };

export interface Knappprops
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'type' | 'children'> {
  readonly variant?: Knappvariant;
  readonly ton?: Knappton;
  readonly ikon?: Ikonnamn;
  /**
   * Skärmläsarens text. OBLIGATORISK för variant 'ikon' och 'rapport' är den
   * det inte — men en ikonknapp utan etikett är en knapp ingen med skärmläsare
   * kan använda, så typen kräver den nedan.
   */
  readonly etikett?: string;
  readonly children?: ReactNode;
}

export const Knapp = ({
  variant = 'stor',
  ton = 'normal',
  ikon,
  etikett,
  children,
  ...ovrigt
}: Knappprops) => {
  const ikonstorlek = variant === 'stor' ? 19 : variant === 'rapport' ? 20 : 16;
  return (
    <button
      type="button"
      className={KLASS[variant] + (variant === 'piller' || variant === 'piller-mini' ? TON[ton] : '')}
      {...(etikett === undefined ? {} : { 'aria-label': etikett })}
      {...ovrigt}
    >
      {ikon !== undefined && <Ikon namn={ikon} storlek={ikonstorlek} tjocklek={1.9} />}
      {children}
    </button>
  );
};

/** En ikonknapp MÅSTE ha en etikett — typen ser till att den finns. */
export const Ikonknapp = ({
  etikett,
  ikon,
  ...ovrigt
}: Omit<Knappprops, 'variant' | 'children' | 'ikon' | 'etikett'> & {
  readonly etikett: string;
  readonly ikon: Ikonnamn;
}) => (
  <Knapp variant="ikon" etikett={etikett} {...ovrigt}>
    <Ikon namn={ikon} storlek={18} tjocklek={2} />
  </Knapp>
);

/** `.quick` — höljet för två-tre 'snabb'-knappar. */
export const Snabbrad = ({ children }: { readonly children: ReactNode }) => (
  <div className="quick">{children}</div>
);

/** `.repgrid` — fyra 'rapport'-knappar i rad på arbetsordern. */
export const Rapportrad = ({ children }: { readonly children: ReactNode }) => (
  <div className="repgrid">{children}</div>
);

/**
 * RÖSTKNAPPEN (`.rostbtn`).
 *
 * BESLUT 02: rösten ingår men BARA ONLINE. Rösttolkningen sker på servern
 * (VOICE_TRANSCRIBE), så knappen ska vara AV utan nät i stället för att spela
 * in något som aldrig kan tolkas. `online` styr det, och texten under säger
 * varför.
 *
 * TVÅ SÄTT ATT ANVÄNDA DEN:
 *   onClick            en vanlig knapp — arbetsordern använder den så för att
 *                      hoppa till tidrapporten.
 *   onHallIn / onSlapp HÅLL-IN-KNAPPEN i tidrapporten: pekaren ned startar
 *                      inspelningen, pekaren upp (eller lämnar knappen, eller
 *                      avbryts av telefonen) stoppar den. `lyssnar` ritar
 *                      knappen i lyssnarläge så att man SER att den hör.
 *
 * VARFÖR POINTER-HÄNDELSER OCH INTE TOUCH: en enda uppsättning som täcker
 * finger, mus och penna. Kontextmenyn stoppas — ett långt tryck på Android
 * öppnar annars "kopiera/markera" mitt i inspelningen — och `touch-action:
 * none` i CSS:en hindrar sidan från att skrolla under fingret.
 */
export const Rostknapp = ({
  online,
  onClick,
  onHallIn,
  onSlapp,
  lyssnar = false,
  disabled = false,
  children = 'Säg det i stället',
}: {
  readonly online: boolean;
  readonly onClick?: () => void;
  readonly onHallIn?: () => void;
  readonly onSlapp?: () => void;
  readonly lyssnar?: boolean;
  readonly disabled?: boolean;
  readonly children?: ReactNode;
}) => {
  const hallIn = onHallIn !== undefined;
  return (
    <button
      type="button"
      className={lyssnar ? 'rostbtn lyssnar' : 'rostbtn'}
      disabled={!online || disabled}
      aria-pressed={hallIn ? lyssnar : undefined}
      {...(onClick === undefined ? {} : { onClick })}
      {...(hallIn
        ? {
            onPointerDown: (e: { preventDefault: () => void }) => {
              e.preventDefault();
              onHallIn();
            },
            onPointerUp: onSlapp,
            onPointerCancel: onSlapp,
            onPointerLeave: onSlapp,
            onContextMenu: (e: { preventDefault: () => void }) => e.preventDefault(),
          }
        : {})}
      title={online ? undefined : 'Rösten behöver nätet. Skriv in tiden så länge.'}
    >
      <Ikon namn="rost" storlek={18} />
      {children}
    </button>
  );
};
